import { useCallback, useEffect, useRef, useState } from "react";

import { draftStore } from "@/features/sistema/services/draftStore";

type Opts<T> = { key: string | null; initial: T; debounceMs?: number; ttlHours?: number; };

function stableHash(o: any) { try { return JSON.stringify(o); } catch { return String(o); } }

export function useDraft<T extends object>({ key, initial, debounceMs, ttlHours = 168 }: Opts<T>) {
  const [value, setValue] = useState<T>(initial);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [pendingSync, setPendingSync] = useState(false);
  const [loaded, setLoaded] = useState(false);
  
  // Estratégia adaptativa de debounce baseada na plataforma e contexto
  const getOptimalDelay = useCallback(() => {
    if (debounceMs) return debounceMs;
    
    const isIOS = draftStore.isIOS();
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isLowMemory = (navigator as any).deviceMemory && (navigator as any).deviceMemory < 4;
    
    if (isIOS) {
      return isPWA ? 200 : 300; // PWA iOS pode ser mais agressivo
    } else {
      return isLowMemory ? 800 : 600; // Android com pouca memória precisa mais tempo
    }
  }, [debounceMs]);
  
  const delay = getOptimalDelay();
  const timer = useRef<number | null>(null);
  const lastHashRef = useRef<string>("");
  const saveAttempts = useRef<number>(0);

  const loadDraft = useCallback(async () => {
    if (!key) { setLoaded(true); return null; }
    const obj = await draftStore.get<any>(key);
    if (!obj?.savedAt) { setLoaded(true); return null; }
    const savedAt = new Date(obj.savedAt);
    const ageH = (Date.now() - savedAt.getTime()) / 36e5;
    if (ageH > ttlHours) { await draftStore.remove(key); setLoaded(true); return null; }
    const { savedAt: _sa, pendingSync: _ps, ...onlyValue } = obj;
    setValue(onlyValue);
    setLastSavedAt(obj.savedAt);
    setPendingSync(!!obj.pendingSync);
    lastHashRef.current = stableHash(onlyValue);
    setLoaded(true);
    return obj;
  }, [key, ttlHours]);

  const scheduleSave = useCallback(async (nextValue: T, forcePending = false) => {
    if (!key) return;
    if (timer.current) window.clearTimeout(timer.current);
    
    timer.current = window.setTimeout(async () => {
      const currentHash = stableHash(nextValue);
      if (currentHash === lastHashRef.current) return; // nada mudou
      
      const payload = { 
        ...(nextValue as any), 
        savedAt: new Date().toISOString(), 
        pendingSync: forcePending || pendingSync,
        saveAttempt: saveAttempts.current++
      };
      
      try {
        await draftStore.set(key, payload);
        lastHashRef.current = currentHash;
        setLastSavedAt(payload.savedAt);
        saveAttempts.current = 0; // reset on success
        console.log(`✅ Draft salvo: ${key} (tentativa ${payload.saveAttempt})`);
      } catch (error) {
        console.warn(`⚠️ Draft falhou: ${key} (tentativa ${saveAttempts.current}):`, error);
        
        // Retry logic com backoff exponencial
        if (saveAttempts.current < 3) {
          const retryDelay = delay * Math.pow(2, saveAttempts.current);
          setTimeout(() => scheduleSave(nextValue, forcePending), retryDelay);
        }
      }
    }, delay) as unknown as number;
  }, [key, delay, pendingSync]);

  const clearDraft = useCallback(async () => {
    if (!key) return;
    if (timer.current) window.clearTimeout(timer.current);
    await draftStore.remove(key);
    lastHashRef.current = "";
    setLastSavedAt(null);
    setPendingSync(false);
  }, [key]);

  // Flush melhorado para iOS e Android
  useEffect(() => {
    const isIOS = draftStore.isIOS();
    
    const flush = async () => {
      if (!key || !lastHashRef.current) return;
      
      const payload = { 
        ...(value as any), 
        savedAt: new Date().toISOString(), 
        pendingSync: true,
        flushedAt: new Date().toISOString()
      };
      
      // Flush imediato e crítico - tentar todas as camadas
      try {
        await draftStore.set(key, payload);
        console.log(`🔄 Flush crítico executado: ${key}`);
      } catch (error) {
        console.error(`❌ Flush crítico falhou: ${key}`, error);
      }
    };
    
    // Eventos essenciais para salvamento
    const events = [
      'visibilitychange',
      'pagehide',
      'beforeunload'
    ];
    
    // iOS específico: adicionar eventos de foco/blur
    if (isIOS) {
      events.push('blur', 'focusout');
    }
    
    const onCriticalEvent = (event: Event) => {
      if (event.type === 'visibilitychange' && document.visibilityState === 'visible') {
        return; // Só flush quando saindo da visibilidade
      }
      flush();
    };
    
    events.forEach(event => {
      if (event === 'beforeunload') {
        window.addEventListener(event, onCriticalEvent);
      } else {
        document.addEventListener(event, onCriticalEvent);
      }
    });
    
    return () => {
      events.forEach(event => {
        if (event === 'beforeunload') {
          window.removeEventListener(event, onCriticalEvent);
        } else {
          document.removeEventListener(event, onCriticalEvent);
        }
      });
    };
  }, [key, value]);

  return { value, setValue, lastSavedAt, pendingSync, setPendingSync, loaded, loadDraft, scheduleSave, clearDraft };
}
